import {
  computed,
  effect,
  Inject,
  Injectable,
  InjectionToken,
  inject,
  signal,
  Signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import {
  ApiHttpResponse,
  Coaching,
  Country,
  GpsOnly,
  Meta,
  Playlist,
  VideoSection,
  Workout,
} from '@kinomap-dev/ng-ui-lib/core';
import { SortDirectionType } from '../types/sort-direction.type';
import { ContentType } from '../pages/content-creation/content-creation';
import {
  HttpParameterCodec,
  HttpParams,
  httpResource,
  HttpResourceRef,
} from '@angular/common/http';
import { environment } from '../../environments/environment';
import { FilterService } from './filters.service';
import { ActiveFilterHttpParam, ActiveFilters } from '../types/filter.type';
import { getActiveFilterHttpParams, getActiveSortHttpParams } from '../utils/filters-utils';
import { ApiService } from './api.service';
import { CountryService } from './country.service';
import { getKinomapFlagUrl } from '../utils/get-kinomap-flag-url';
import { getCountryCode } from '../utils/get-country-code';
import { ContentCountryItem } from '../types/country-viewer-repartition.type';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PaginatorService } from './paginator-service';
import { PAGINATOR_ITEMS_PER_PAGE_OPTIONS } from '../components/paginator/paginator';
import { getFilterQueryParams } from '../utils/filters-query-params';

export const CONTENT_TYPE_TOKEN: InjectionToken<ContentType> = new InjectionToken<ContentType>(
  'contentType',
);

type ContentDataType = VideoSection[] | Workout[] | GpsOnly[] | Coaching[] | Playlist[];

const PAGE_QUERY_PARAM: string = 'page';
const LIMIT_QUERY_PARAM: string = 'limit';
const DEFAULT_PAGE: number = 1;
const DEFAULT_LIMIT: number = 10;

@Injectable()
export class TableContentService {
  private contentType: ContentType;
  private readonly filterService: FilterService = inject(FilterService);
  private readonly apiService: ApiService = inject(ApiService);
  private readonly countryService: CountryService = inject(CountryService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly paginatorService: PaginatorService = inject(PaginatorService);

  public currentPage: WritableSignal<number> = signal<number>(this.readPageFromUrl());
  public sortDirection: WritableSignal<SortDirectionType> = signal('asc');
  public contentLimitPerPage: WritableSignal<number> = signal<number>(this.readLimitFromUrl());

  private readonly resource: HttpResourceRef<ApiHttpResponse<ContentDataType> | undefined>;
  private readonly contentCountriesResource: HttpResourceRef<
    ApiHttpResponse<ContentCountryItem[]> | undefined
  >;
  private readonly contentUploadDateRef: HttpResourceRef<string[] | undefined> =
    this.apiService.getContentUploadedDate();

  public readonly tableData: Signal<ApiHttpResponse<ContentDataType> | undefined>;

  public totalItems: Signal<number> = computed<number>(
    () => (this.tableData()?.meta as Meta)?.total || 0,
  );
  public lastPage: Signal<number> = computed<number>(
    () => (this.tableData()?.meta as Meta)?.last_page || 0,
  );
  public firstItemInPage: Signal<number> = computed<number>(
    () => (this.tableData()?.meta as Meta)?.from || 0,
  );
  public lastItemInPage: Signal<number> = computed<number>(
    () => (this.tableData()?.meta as Meta)?.to || 0,
  );

  constructor(@Inject(CONTENT_TYPE_TOKEN) contentType: ContentType) {
    this.contentType = contentType;
    this.filterService.applyQueryParams(this.route.snapshot.queryParamMap);
    this.contentCountriesResource = this.apiService.getContentCountries(this.contentType);
    this.resource = httpResource<ApiHttpResponse<ContentDataType>>(() => ({
      url: this.getUrl(),
      method: 'GET',
      params: this.buildParams(),
    }));
    this.tableData = this.resource.value;

    effect(() => {
      const queryParams: Params = {
        [PAGE_QUERY_PARAM]: this.currentPage(),
        [LIMIT_QUERY_PARAM]: this.contentLimitPerPage(),
        ...getFilterQueryParams(
          this.filterService.filter(),
          this.filterService.activeSort(),
          this.filterService.pendingActiveValues(),
        ),
      };

      untracked(() => this.syncUrl(queryParams));
    });

    effect(() => {
      const contentCountries: ContentCountryItem[] | undefined =
        this.contentCountriesResource.value()?.data;
      const countriesList: Country[] | undefined = this.countryService.countries();
      if (!contentCountries) return;

      untracked(() => {
        if (!contentCountries || !countriesList) return;
        this.filterService.setFilterData(
          'country',
          0,
          contentCountries.map((country: ContentCountryItem) => ({
            label: `${country.cnt_name} (${country.content_count})`,
            iconUrl: getKinomapFlagUrl(country, countriesList),
            value: getCountryCode(country, countriesList)!,
          })),
        );
      });
    });

    effect(() => {
      const contentUploadDate: string[] | undefined = this.contentUploadDateRef.value();

      untracked(() => {
        if (!contentUploadDate) return;
        this.filterService.setFilterData(
          'upload',
          0,
          contentUploadDate.map((year: string) => ({
            label: `${year}`,
            value: `${year}`,
          })),
        );
      });
    });
  }

  private getUrl(): string {
    switch (this.contentType) {
      case 'scenic':
        return `${environment.apiUrl}me/scenics`;
      case 'workout':
        return `${environment.apiUrl}me/workouts`;
      case 'gps-only':
        return `${environment.apiUrl}me/mapmodes`;
      case 'coaching':
        return `${environment.apiUrl}me/coachings`;
      case 'playlist':
        return `${environment.apiUrl}me/playlists`;
      default:
        return `${environment.apiUrl}me/workouts`;
    }
  }

  nextPage(): void {
    this.currentPage.update((v: number) => (v += 1));
  }

  previousPage(): void {
    this.currentPage.update((v: number) => (v -= 1));
  }

  goToPage(page: number) {
    this.currentPage.set(page);
  }

  setItemPerPage(value: number): void {
    this.contentLimitPerPage.set(value);
    this.currentPage.set(1);
  }

  private readPageFromUrl(): number {
    const page: number = Number(this.route.snapshot.queryParamMap.get(PAGE_QUERY_PARAM));
    return Number.isInteger(page) && page > 0 ? page : DEFAULT_PAGE;
  }

  private readLimitFromUrl(): number {
    const limit: number = Number(this.route.snapshot.queryParamMap.get(LIMIT_QUERY_PARAM));
    return PAGINATOR_ITEMS_PER_PAGE_OPTIONS.includes(limit) ? limit : DEFAULT_LIMIT;
  }

  /**
   * The params are replaced as a whole rather than merged, so that clearing a
   * filter actually drops it from the URL.
   */
  private syncUrl(queryParams: Params): void {
    this.paginatorService.setListQueryParams(this.contentType, queryParams);

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  private buildParams(): HttpParams {
    let params: HttpParams = new HttpParams({ encoder: new BracketHttpParameterCodec() })
      .set('page', this.currentPage())
      .set('limit', this.contentLimitPerPage())
      .set('sortDirection', this.sortDirection());

    params = this.appendActiveFiltersParams(params, this.filterService.activeFilters());

    return this.appendActiveSortParams(params);
  }

  private appendActiveFiltersParams(params: HttpParams, activeFilters: ActiveFilters): HttpParams {
    return getActiveFilterHttpParams(activeFilters).reduce(
      (httpParams: HttpParams, param: ActiveFilterHttpParam) =>
        httpParams.append(param.name, param.value),
      params,
    );
  }

  private appendActiveSortParams(params: HttpParams): HttpParams {
    return getActiveSortHttpParams(this.filterService.activeSort()).reduce(
      (httpParams: HttpParams, param: ActiveFilterHttpParam) =>
        httpParams.set(param.name, param.value),
      params,
    );
  }
}

export class BracketHttpParameterCodec implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key).replace(/%5B/g, '[').replace(/%5D/g, ']');
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}
